import { type IngredientWithPortion } from "@fitness-app/data-models/entities/Ingredient";
import { type DishInMeal, type NutritionPlanDay } from "@fitness-app/data-models/entities/MealsPlan";

/**
 * TRE-2033 — the other half of "is this tracked day worth keeping".
 *
 * `hasClientInputInNutritionDay` answers "did she log anything here" (ate, skipped, added a dish,
 * left a note). It misses the way clients actually prepare a week ahead: swapping dishes. A swap
 * goes through `replaceDishInNutritionDay`, which deliberately resets the incoming dish to
 * `status: EMPTY` with an empty note — so an arranged week is indistinguishable from the
 * touched-then-reverted leftover the plan-swap cleanup is meant to drop, and 111 of the 121
 * future days on prod that qualified for deletion turned out to be arrangements.
 *
 * So the cleanup asks this question instead: is the day still literally the plan day it was
 * snapshotted from? Only then is it a copy carrying no information. Anything the client
 * rearranged makes the fingerprints differ and the day is kept.
 *
 * The fingerprint is (meal position, meal id, dish position, dish id, name, portion,
 * portionCount, parentId). Name, portion and parentId do the real work: a swap KEEPS the id of
 * the slot it replaces (see `replaceDishInNutritionDay`), so the id alone would notice nothing —
 * verified on prod, where a swapped breakfast kept its dish id and changed only name and portion.
 * `parentId` covers the swap that lands on a dish with the same name and portion (the same recipe
 * pulled from an earlier plan is a different instance), which name and portion cannot see.
 *
 * `status`, `clientNote` and `fromTracker` are deliberately NOT part of it; those belong to
 * `hasClientInputInNutritionDay` and the two are meant to be used together:
 *
 *     !hasClientInputInNutritionDay(day) && isUntouchedCopyOfPlanDay(day, planDay)
 *
 * `PgNutritionRepositories.dropUntouchedTrackedDaysOfReplacedPlan` builds the same fingerprint in
 * SQL for the cron path — the two must be changed together. Each comparison happens within one
 * engine (snapshot vs template), so they never have to agree on number formatting with each other.
 */

const field = (value: unknown) => (value === null || value === undefined ? "" : String(value));

const dishFingerprint = (
  meal: NutritionPlanDay["meals"][number],
  mealIndex: number,
  dish: DishInMeal | IngredientWithPortion,
  dishIndex: number,
) =>
  [
    mealIndex + 1,
    field(meal.id),
    dishIndex + 1,
    field(dish.id),
    field(dish.name),
    field(dish.portion),
    field("portionCount" in dish ? dish.portionCount : undefined),
    field("parentId" in dish ? dish.parentId : undefined),
  ].join("|");

// One row per dish, and one empty row for a meal with no dishes, so that meals cannot silently
// vanish from the fingerprint. Mirrors the SQL `left join lateral` over dishes.
const dayFingerprint = (day: NutritionPlanDay) =>
  (day.meals || [])
    .flatMap((meal, mealIndex) => {
      const dishes = meal.dishes || [];

      return dishes.length
        ? dishes.map((dish, dishIndex) => dishFingerprint(meal, mealIndex, dish, dishIndex))
        : [[mealIndex + 1, field(meal.id), "", "", "", "", "", ""].join("|")];
    })
    .join(";");

export const isUntouchedCopyOfPlanDay = (
  day: NutritionPlanDay | null | undefined,
  planDay: NutritionPlanDay | null | undefined,
): boolean => {
  if (!day || !planDay) {
    return false;
  }

  return dayFingerprint(day) === dayFingerprint(planDay);
};

/**
 * The plan day a tracked day was snapshotted from. The snapshot copies the template day verbatim,
 * id included, so it points at exactly one day of exactly one plan — no weekday guessing. Returns
 * null when the plan no longer holds it, and the caller must then keep the row: an unanswerable
 * question is not a licence to delete.
 */
export const findSourcePlanDay = (
  day: NutritionPlanDay | null | undefined,
  planDays: NutritionPlanDay[],
): NutritionPlanDay | null => (day?.id ? (planDays.find((planDay) => planDay.id === day.id) ?? null) : null);
